import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Api } from '../../services/Api/Api';

const EditAnnonceModal = ({ isOpen, onClose, onSuccess, annonce }) => {
    const [formData, setFormData] = useState({
        nom_societe: '',
        forme_juridique: '',
        siege_social: '',
        capital_social: '',
        devise: 'XOF',
        duree: '',
        objet_social: '',
        gerant: '',
        contenu_complet: '',
        date_publication: ''
    });
    const [loading, setLoading] = useState(false);

    // Extraire les informations depuis les données API
    const extractInfoFromContent = (content, field) => {
        if (!content) return '';

        const patterns = {
            nom_societe: /dénommée\s*:\s*([^.\n]+)/i,
            forme_juridique: /(Société Civile Immobilière|Société à Responsabilité Limitée Unipersonnelle|Société par Actions Simplifiée Unipersonnelle|Société à Responsabilité Limitée|SARL|SA|SAS|SASU|EURL|SCI|SNC)/i,
            siege_social: /\*\*Siège\s*:\*\*\s*([^\n]+)/i,
            capital_social: /\*\*Capital\s*social\s*:\*\*\s*([^\n]+)/i,
            duree: /\*\*Durée\s*:\*\*\s*([^\n]+)/i,
            gerant: /\*\*Gérant\s*:\*\*\s*([^\n]+)/i,
            objet_social: /\*\*objet\*\*\s*:(.*?)(?=\*\*|Et pour la réalisation)/is
        };

        const matches = content.match(patterns[field]);
        return matches ? matches[1].trim() : '';
    };

    // Charger les données de l'annonce quand le modal s'ouvre
    useEffect(() => {
        if (isOpen && annonce) {
            // Utiliser les champs directs s'ils existent, sinon extraire du contenu
            const contentToExtract = annonce.contenu_complet || '';

            setFormData({
                nom_societe: annonce.nom_societe || extractInfoFromContent(contentToExtract, 'nom_societe'),
                forme_juridique: annonce.forme_juridique ||
                    annonce.immatriculation_details?.forme_juridique_libelle ||
                    extractInfoFromContent(contentToExtract, 'forme_juridique'),
                siege_social: annonce.siege_social || extractInfoFromContent(contentToExtract, 'siege_social'),
                capital_social: annonce.capital_social !== "0.00" ? annonce.capital_social :
                    extractInfoFromContent(contentToExtract, 'capital_social').replace(/[^\d]/g, ''),
                devise: annonce.devise || 'XOF',
                duree: annonce.duree || extractInfoFromContent(contentToExtract, 'duree'),
                objet_social: annonce.objet_social || extractInfoFromContent(contentToExtract, 'objet_social'),
                gerant: annonce.gerant || extractInfoFromContent(contentToExtract, 'gerant'),
                contenu_complet: annonce.contenu_complet || '',
                date_publication: annonce.date_publication || ''
            });
        }
    }, [isOpen, annonce]);

    // Réinitialiser le formulaire
    const resetForm = () => {
        setFormData({
            nom_societe: '',
            forme_juridique: '',
            siege_social: '',
            capital_social: '',
            devise: 'XOF',
            duree: '',
            objet_social: '',
            gerant: '',
            contenu_complet: '',
            date_publication: ''
        });
    };

    // Gérer les changements dans le formulaire
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Régénérer le contenu complet basé sur les champs du formulaire
    const generateContenuComplet = () => {
        const template = `Abidjan Cocody Les Deux Plateaux, 7e tranche, 28 BP 1676 Abidjan 28  
TEL : 07 07 59 35 95/ 27 24 35 16 23  
E-MAIL : Contact@legafrik.com  

Suivant les DSV et Statuts, établis par actes sous seing privé par Legafrik, il a été constitué une ${formData.forme_juridique} dénommée : ${formData.nom_societe}.

Ayant pour **objet** :  
${formData.objet_social}

Et pour la réalisation de l'objet social, l'acquisition, la location et la vente de tous biens meubles et immeubles ;  
l'emprunt de toutes sommes auprès de tout établissement financier avec possibilité de donner en garantie tout ou partie des biens sociaux ;  
la prise en location-gérance de tous fonds de commerce ;  
la prise de participation dans toute société existante ou devant être créée ;  
et généralement, toutes opérations financières, commerciales, industrielles, mobilières et immobilières, se rapportant directement ou indirectement à l'objet social ou pouvant en faciliter l'extension ou le développement.  

**Siège :** ${formData.siege_social}  
**Durée :** ${formData.duree}  

**Capital social :** ${formData.capital_social} ${formData.devise}  
**Gérant :** ${formData.gerant}  

Dépôt des actes constitutifs au Tribunal de Commerce d'Abidjan pour l'immatriculation au RCCM sous le numéro : ${annonce?.immatriculation_details?.reference || '[RCCM À COMPLÉTER]'}.

**POUR AVIS :**  
Legafrik`;

        return template;
    };

    // Modifier une annonce existante
    const updateAnnonce = async () => {
        try {
            setLoading(true);

            // Générer le contenu complet si nécessaire
            const updatedContenuComplet = formData.contenu_complet || generateContenuComplet();

            const payload = {
                nom_societe: formData.nom_societe,
                forme_juridique: formData.forme_juridique,
                siege_social: formData.siege_social,
                capital_social: formData.capital_social,
                devise: formData.devise,
                duree: formData.duree,
                objet_social: formData.objet_social,
                gerant: formData.gerant,
                contenu_complet: updatedContenuComplet,
                date_publication: formData.date_publication
            };

            await Api.put(`formalite/annonces/${annonce.id}`, payload);
            resetForm();
            onSuccess('Annonce modifiée avec succès !');
        } catch (error) {
            console.error('Erreur lors de la modification:', error);
            alert('Erreur lors de la modification de l\'annonce');
        } finally {
            setLoading(false);
        }
    };

    // Gérer la soumission du formulaire
    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation simple
        if (!formData.nom_societe || !formData.forme_juridique) {
            alert('Veuillez remplir au minimum le nom de la société et la forme juridique');
            return;
        }

        updateAnnonce();
    };

    // Gérer la fermeture du modal
    const handleClose = () => {
        if (!loading) {
            resetForm();
            onClose();
        }
    };

    // Générer automatiquement le contenu complet
    const handleGenerateContent = () => {
        const generatedContent = generateContenuComplet();
        setFormData(prev => ({
            ...prev,
            contenu_complet: generatedContent
        }));
    };

    if (!isOpen || !annonce) return null;

    const determineCategorie = () => {
        const content = annonce.contenu_complet || '';
        if (content.includes('MODIFICATION') || content.includes('modification')) return 'Modification';
        if (content.includes('RADIATION') || content.includes('radiation')) return 'Radiation';
        if (content.includes('constitué') || content.includes('CONSTITUTION')) return 'Immatriculation';
        return 'Immatriculation'; // Par défaut
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content modal-large">
                <div className="modal-header">
                    <h3>Modifier l'annonce</h3>
                    <button
                        className="modal-close"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    {/* Informations de base */}
                    <div className="form-section">
                        <h4>Informations de base</h4>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="nom_societe">Nom de la société *</label>
                                <input
                                    type="text"
                                    id="nom_societe"
                                    name="nom_societe"
                                    value={formData.nom_societe}
                                    onChange={handleInputChange}
                                    required
                                    disabled={loading}
                                    placeholder="Nom de la société"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="forme_juridique">Forme juridique *</label>
                                <select
                                    id="forme_juridique"
                                    name="forme_juridique"
                                    value={formData.forme_juridique}
                                    onChange={handleInputChange}
                                    required
                                    disabled={loading}
                                >
                                    <option value="">Sélectionner une forme</option>
                                    <option value="Société à Responsabilité Limitée">SARL</option>
                                    <option value="Société Anonyme">SA</option>
                                    <option value="Société par Actions Simplifiée">SAS</option>
                                    <option value="Société par Actions Simplifiée Unipersonnelle">SASU</option>
                                    <option value="Société à Responsabilité Limitée Unipersonnelle">SARLU</option>
                                    <option value="Société Civile Immobilière">SCI</option>
                                    <option value="Société en Nom Collectif">SNC</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="siege_social">Siège social</label>
                                <input
                                    type="text"
                                    id="siege_social"
                                    name="siege_social"
                                    value={formData.siege_social}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                    placeholder="Adresse du siège social"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="gerant">Gérant</label>
                                <input
                                    type="text"
                                    id="gerant"
                                    name="gerant"
                                    value={formData.gerant}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                    placeholder="Nom du gérant"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Informations financières et durée */}
                    <div className="form-section">
                        <h4>Informations financières</h4>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="capital_social">Capital social</label>
                                <input
                                    type="number"
                                    id="capital_social"
                                    name="capital_social"
                                    value={formData.capital_social}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                    placeholder="1000000"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="devise">Devise</label>
                                <select
                                    id="devise"
                                    name="devise"
                                    value={formData.devise}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                >
                                    <option value="XOF">FCFA (XOF)</option>
                                    <option value="EUR">Euro (EUR)</option>
                                    <option value="USD">Dollar US (USD)</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="duree">Durée</label>
                                <input
                                    type="text"
                                    id="duree"
                                    name="duree"
                                    value={formData.duree}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                    placeholder="99 ans"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="date_publication">Date de publication</label>
                                <input
                                    type="date"
                                    id="date_publication"
                                    name="date_publication"
                                    value={formData.date_publication}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Objet social */}
                    <div className="form-section">
                        <h4>Objet social</h4>
                        <div className="form-group">
                            <label htmlFor="objet_social">Activités de l'entreprise</label>
                            <textarea
                                id="objet_social"
                                name="objet_social"
                                value={formData.objet_social}
                                onChange={handleInputChange}
                                rows="4"
                                disabled={loading}
                                placeholder="Décrivez les activités principales de l'entreprise..."
                            />
                        </div>
                    </div>

                    {/* Contenu complet */}
                    <div className="form-section">
                        <div className="section-header">
                            <h4>Contenu complet de l'annonce</h4>
                            <button
                                type="button"
                                className="action-button small"
                                onClick={handleGenerateContent}
                                disabled={loading}
                            >
                                Générer automatiquement
                            </button>
                        </div>

                        <div className="form-group">
                            <label htmlFor="contenu_complet">Texte de l'annonce légale</label>
                            <textarea
                                id="contenu_complet"
                                name="contenu_complet"
                                value={formData.contenu_complet}
                                onChange={handleInputChange}
                                rows="12"
                                disabled={loading}
                                placeholder="Le contenu complet de l'annonce légale..."
                            />
                            <small className="form-hint">
                                Vous pouvez modifier manuellement le texte ou utiliser "Générer automatiquement" pour créer le contenu basé sur les informations saisies ci-dessus.
                            </small>
                        </div>
                    </div>

                    {/* Informations du système */}
                    <div className="form-section readonly-section">
                        <h4>Informations système</h4>
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">ID:</span>
                                <span className="info-value">{annonce.id}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Statut:</span>
                                <span className="info-value">
                                    {annonce.statut === 'brouillon' ? 'En attente' : annonce.statut}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Catégorie détectée:</span>
                                <span className="info-value">{determineCategorie()}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Référence RCCM:</span>
                                <span className="info-value">
                                    {annonce.immatriculation_details?.reference || 'N/A'}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Tribunal:</span>
                                <span className="info-value">
                                    {annonce.immatriculation_details?.tribunal_nom || 'N/A'}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Créé le:</span>
                                <span className="info-value">
                                    {annonce.created_at ?
                                        new Date(annonce.created_at).toLocaleDateString('fr-FR') :
                                        'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="save-button"
                            disabled={loading}
                        >
                            <Save size={16} />
                            {loading ? 'Modification en cours...' : 'Sauvegarder'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditAnnonceModal;